import { useMemo } from "react";
import { useTranslations } from "next-intl";
import type { DialogStepperStep } from "@/components/common/dialog-stepper";
import type { DialogStep } from "./submit-inquiry";

export function useInquiryPresentation({
  step,
  startedUnauthenticated,
  isLoggedIn,
  showProfileInStepper,
  showBuyerProfileInStepper,
}: {
  step: DialogStep;
  startedUnauthenticated: boolean;
  isLoggedIn: boolean;
  showProfileInStepper: boolean;
  showBuyerProfileInStepper: boolean;
}) {
  const t = useTranslations();
  function getTitle() {
    switch (step) {
      case "form":
      case "submitting":
        return t("saas.listing.sidebar.contactLoggedIn.title");
      case "auth_start":
        return t("saas.auth.home.title");
      case "auth_complete_profile":
        return t("saas.auth.signup.title");
      case "auth_otp":
        return t("saas.auth.otp.title");
      case "buyer_profile":
        return "Almost there!";
      case "success":
        return t("saas.listing.sidebar.sent.title");
      default:
        return "";
    }
  }

  function showBackButton() {
    return step === "auth_start";
  }

  const stepperSteps = useMemo<DialogStepperStep[]>(() => {
    const showAuthSteps = startedUnauthenticated || !isLoggedIn;
    const base: DialogStepperStep[] = [{ key: "form", label: "Message" }];
    if (showAuthSteps) {
      base.push(
        { key: "auth_start", label: "Sign in" },
        { key: "auth_otp", label: "Verify" },
      );
    }
    if (showProfileInStepper) {
      base.push({ key: "auth_complete_profile", label: "Profile" });
    }
    if (showBuyerProfileInStepper) {
      base.push({ key: "buyer_profile", label: "About you" });
    }
    base.push({ key: "submitting", label: "Send" });
    return base;
  }, [
    isLoggedIn,
    startedUnauthenticated,
    showProfileInStepper,
    showBuyerProfileInStepper,
  ]);

  const stepperCurrentStep = step === "success" ? "submitting" : step;

  const showStepper = step !== "success";

  return {
    getTitle,
    showBackButton,
    stepperSteps,
    stepperCurrentStep,
    showStepper,
  };
}
